import type { ReactNode } from 'react';
import type { AdminWorldState } from '../../../lib/api';
import { PlasticityRadar, type PlasticityAxisKey } from '../PlasticityRadar';

interface WorldCorrespondentProps {
  state: AdminWorldState;
  previousState: AdminWorldState | null;
}

export function WorldCorrespondent({ state, previousState }: WorldCorrespondentProps) {
  const correspondentState = (state.correspondent ?? null) as Record<string, unknown> | null;
  const correspondentProfile = (correspondentState?.profile ?? null) as Record<string, unknown> | null;
  const correspondentInnerDialog = (correspondentState?.inner_dialog ?? null) as Record<string, unknown> | null;
  const styleProfile = (state.style as { style_profile?: Record<string, unknown> } | null)?.style_profile;
  const evolvedTraits = (styleProfile?.evolved_traits ?? []) as Array<Record<string, unknown>>;
  const previousStyleProfile = (previousState?.style as { style_profile?: Record<string, unknown> } | null)?.style_profile;
  const previousTraits = (previousStyleProfile?.evolved_traits ?? []) as Array<Record<string, unknown>>;

  if (!correspondentProfile) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-xl text-parchment">Correspondent</h1>
          <p className="text-mist text-sm mt-1">Character profile and inner dialog</p>
        </div>
        <div className="text-center py-12 text-mist">
          No correspondent profile captured yet.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-xl text-parchment">Correspondent</h1>
        <p className="text-mist text-sm mt-1">Character profile and inner dialog</p>
      </div>

      <CorrespondentProfileView
        profile={correspondentProfile}
        innerDialog={correspondentInnerDialog}
        evolvedTraits={evolvedTraits}
        previousTraits={previousTraits}
      />
    </div>
  );
}

// Helper functions
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' && value.trim().length === 0) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '—';
}

function ChipList({ items }: { items: unknown }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <span className="text-mist/60">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, idx) => (
        <span
          key={`${String(item)}-${idx}`}
          className="px-2 py-1 rounded-full text-xs bg-stone/40 text-parchment"
        >
          {typeof item === 'string' ? item : String(item)}
        </span>
      ))}
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-mist text-xs uppercase tracking-wide">{label}</span>
      <span className="text-parchment text-sm text-right">{formatValue(value)}</span>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-void border border-stone/30 rounded-xl p-4 space-y-3">
      <div className="text-sm font-semibold text-parchment">{title}</div>
      {children}
    </div>
  );
}

function getSection(
  profile: Record<string, unknown> | null,
  key: string,
): Record<string, unknown> | null {
  if (!profile) return null;
  const section = profile[key];
  if (!section || typeof section !== 'object') return null;
  return section as Record<string, unknown>;
}

const PLASTICITY_AXIS_IDS: Record<PlasticityAxisKey, string> = {
  guardedness: 'plasticity_axis_guardedness',
  warmth: 'plasticity_axis_warmth',
  directness: 'plasticity_axis_directness',
  trust: 'plasticity_axis_trust',
  curiosity: 'plasticity_axis_curiosity',
};

function getPlasticityAxes(
  traits: Array<Record<string, unknown>> | null,
): Record<PlasticityAxisKey, number> {
  const lookup = new Map<string, number>();
  for (const trait of traits ?? []) {
    const id = typeof trait.id === 'string' ? trait.id : '';
    const intensity = typeof trait.intensity === 'number' ? trait.intensity : undefined;
    if (id && intensity !== undefined) {
      lookup.set(id, intensity);
    }
  }

  const clamp = (value: number) => Math.min(1, Math.max(0, value));

  return {
    guardedness: clamp(lookup.get(PLASTICITY_AXIS_IDS.guardedness) ?? 0.5),
    warmth: clamp(lookup.get(PLASTICITY_AXIS_IDS.warmth) ?? 0.5),
    directness: clamp(lookup.get(PLASTICITY_AXIS_IDS.directness) ?? 0.5),
    trust: clamp(lookup.get(PLASTICITY_AXIS_IDS.trust) ?? 0.5),
    curiosity: clamp(lookup.get(PLASTICITY_AXIS_IDS.curiosity) ?? 0.5),
  };
}

function getPlasticityTrends(
  current: Record<PlasticityAxisKey, number>,
  previous: Record<PlasticityAxisKey, number> | null,
): Partial<Record<PlasticityAxisKey, number>> {
  if (!previous) return {};
  return {
    guardedness: current.guardedness - previous.guardedness,
    warmth: current.warmth - previous.warmth,
    directness: current.directness - previous.directness,
    trust: current.trust - previous.trust,
    curiosity: current.curiosity - previous.curiosity,
  };
}

function CorrespondentProfileView({
  profile,
  innerDialog,
  evolvedTraits,
  previousTraits,
}: {
  profile: Record<string, unknown> | null;
  innerDialog: Record<string, unknown> | null;
  evolvedTraits: Array<Record<string, unknown>>;
  previousTraits: Array<Record<string, unknown>>;
}) {
  if (!profile) {
    return <div className="text-mist text-sm">No correspondent profile captured yet.</div>;
  }

  const core = getSection(profile, 'core_identity');
  const voice = getSection(profile, 'voice');
  const role = getSection(profile, 'role');
  const conflicts = getSection(profile, 'conflict_arc');
  const dialogSummary = innerDialog?.inner_summary;
  const dialogEmotion = innerDialog?.emotional_weather;
  const dialogSummaryText = formatValue(dialogSummary);
  const dialogThoughts = (innerDialog?.ongoing_thoughts as string[] | undefined) ?? [];
  const dialogWorries = (innerDialog?.private_worries as string[] | undefined) ?? [];
  const dialogHopes = (innerDialog?.private_hopes as string[] | undefined) ?? [];
  const dialogQuestions = (innerDialog?.unresolved_questions as string[] | undefined) ?? [];
  const plasticityAxes = getPlasticityAxes(evolvedTraits);
  const previousAxes = previousTraits.length > 0 ? getPlasticityAxes(previousTraits) : null;
  const plasticityTrends = getPlasticityTrends(plasticityAxes, previousAxes);

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-void via-night to-void border border-stone/30 rounded-xl p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-mist">Correspondent</div>
            <div className="text-2xl font-display text-parchment">{formatValue(core?.name)}</div>
            <div className="text-mist text-sm">
              {formatValue(core?.origin)} · {formatValue(core?.status)}
            </div>
            {dialogSummaryText !== '—' && (
              <div className="text-mist text-sm mt-2">{dialogSummaryText}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <span className="px-3 py-1 rounded-full text-xs bg-rune/20 text-rune">
              {formatValue(role?.narrative_role)}
            </span>
            <span className="px-3 py-1 rounded-full text-xs bg-sky-500/20 text-sky-200">
              {formatValue(voice?.humor)}
            </span>
            <span className="px-3 py-1 rounded-full text-xs bg-amber-500/20 text-amber-200">
              {formatValue(conflicts?.arc_direction)}
            </span>
          </div>
        </div>
      </div>

      {/* Plasticity Radar */}
      <SectionCard title="Plasticity Drift">
        <PlasticityRadar axes={plasticityAxes} trends={plasticityTrends} />
      </SectionCard>

      {/* Profile Sections Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Inner Dialog">
          <FieldRow label="Emotional Weather" value={dialogEmotion} />
          <FieldRow label="Inner Summary" value={dialogSummary} />
          <div className="space-y-2 text-sm text-parchment">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Ongoing Thoughts</div>
              <ChipList items={dialogThoughts} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Private Worries</div>
              <ChipList items={dialogWorries} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Private Hopes</div>
              <ChipList items={dialogHopes} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Unresolved Questions</div>
              <ChipList items={dialogQuestions} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Core Identity">
          <FieldRow label="Age" value={core?.age} />
          <FieldRow label="Gender" value={core?.gender} />
          <FieldRow label="Species" value={core?.species} />
          <FieldRow label="Birthplace" value={core?.birthplace} />
          <FieldRow label="Social Class" value={core?.social_class} />
          <FieldRow label="Public Persona" value={core?.public_persona} />
          <FieldRow label="Private Self" value={core?.private_self} />
        </SectionCard>

        <SectionCard title="Voice & Expression">
          <div className="space-y-2 text-sm text-parchment">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Tone</div>
              <ChipList items={voice?.tone} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Vocabulary</div>
              <ChipList items={voice?.vocabulary} />
            </div>
            <FieldRow label="Rhythm" value={voice?.rhythm} />
            <FieldRow label="Humor" value={voice?.humor} />
          </div>
        </SectionCard>

        <SectionCard title="Physicality & Presence">
          <FieldRow label="Build" value={getSection(profile, 'physicality')?.build} />
          <FieldRow label="Height" value={getSection(profile, 'physicality')?.height} />
          <FieldRow label="Clothing" value={getSection(profile, 'physicality')?.clothing} />
          <FieldRow label="Posture" value={getSection(profile, 'physicality')?.posture} />
          <div>
            <div className="text-xs text-mist uppercase tracking-wide">Features</div>
            <ChipList items={getSection(profile, 'physicality')?.features} />
          </div>
          <div>
            <div className="text-xs text-mist uppercase tracking-wide">Artifacts</div>
            <ChipList items={getSection(profile, 'physicality')?.artifacts} />
          </div>
        </SectionCard>

        <SectionCard title="Psychology & Inner World">
          <FieldRow label="Temperament" value={getSection(profile, 'psychology')?.temperament} />
          <FieldRow label="Emotional Range" value={getSection(profile, 'psychology')?.emotional_range} />
          <FieldRow label="Self Image" value={getSection(profile, 'psychology')?.self_image} />
          <div>
            <div className="text-xs text-mist uppercase tracking-wide">Dominant Traits</div>
            <ChipList items={getSection(profile, 'psychology')?.traits_dominant} />
          </div>
          <div>
            <div className="text-xs text-mist uppercase tracking-wide">Fears & Anxieties</div>
            <ChipList items={getSection(profile, 'psychology')?.anxieties} />
          </div>
        </SectionCard>

        <SectionCard title="Desires & Motivation">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Goals</div>
              <ChipList items={getSection(profile, 'desire_motivation')?.conscious_goals} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Impulses</div>
              <ChipList items={getSection(profile, 'desire_motivation')?.short_term_impulses} />
            </div>
            <FieldRow label="Obsession" value={getSection(profile, 'desire_motivation')?.obsession} />
          </div>
        </SectionCard>

        <SectionCard title="Values & Ethics">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Principles</div>
              <ChipList items={getSection(profile, 'values')?.principles} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Taboos</div>
              <ChipList items={getSection(profile, 'values')?.taboos} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Lines Not Crossed</div>
              <ChipList items={getSection(profile, 'values')?.lines_not_cross} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Skills & Competence">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Learned</div>
              <ChipList items={getSection(profile, 'skills')?.learned} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Innate</div>
              <ChipList items={getSection(profile, 'skills')?.innate} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Profession</div>
              <ChipList items={getSection(profile, 'skills')?.profession} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Flaws & Limitations">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Flaws</div>
              <ChipList items={getSection(profile, 'flaws')?.flaws} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Wounds</div>
              <ChipList items={getSection(profile, 'flaws')?.wounds} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="History & Backstory">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Formative Events</div>
              <ChipList items={getSection(profile, 'history')?.formative_events} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Unresolved Conflicts</div>
              <ChipList items={getSection(profile, 'history')?.unresolved_conflicts} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Relationships & Dynamics">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Family</div>
              <ChipList items={getSection(profile, 'relationships')?.family} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Allies</div>
              <ChipList items={getSection(profile, 'relationships')?.allies} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Role in Story">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Themes</div>
              <ChipList items={role?.themes} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Challenges</div>
              <ChipList items={role?.challenges_to_others} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Conflict & Arc">
          <FieldRow label="Internal Conflict" value={conflicts?.internal_conflict} />
          <FieldRow label="Arc Direction" value={conflicts?.arc_direction} />
          <div>
            <div className="text-xs text-mist uppercase tracking-wide">External Pressures</div>
            <ChipList items={conflicts?.external_pressures} />
          </div>
        </SectionCard>

        <SectionCard title="Symbolic Layer">
          <div className="space-y-2">
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Motifs</div>
              <ChipList items={getSection(profile, 'symbolic_layer')?.motifs} />
            </div>
            <div>
              <div className="text-xs text-mist uppercase tracking-wide">Imagery</div>
              <ChipList items={getSection(profile, 'symbolic_layer')?.imagery} />
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Contradictions */}
      <SectionCard title="Contradictions & Tensions">
        <ChipList items={profile.contradictions} />
      </SectionCard>
    </div>
  );
}
